import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Grid,
  Divider,
  Stack,
  CircularProgress,
  Alert,
  Chip,
  Collapse,
  Paper,
} from '@mui/material';
import {
  ArrowBack,
  Refresh,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  Code as CodeIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { validationService } from '../../services/validationService';
import ValidationStatusBadge from '../../components/validation/ValidationStatusBadge';
import ValidationFindingsList from '../../components/validation/ValidationFindingsList';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ErrorMessage from '../../components/common/ErrorMessage';

const ValidationDetailPage: React.FC = () => {
  const { runId } = useParams<{ runId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: validationRun, isLoading, error } = useQuery({
    queryKey: ['validationRun', runId],
    queryFn: () => validationService.getValidationRun(Number(runId)),
    enabled: !!runId,
    refetchInterval: (query) => {
      // Auto-refresh while running
      const data = query.state.data;
      return data?.status === 'RUNNING' || data?.status === 'PENDING' ? 5000 : false;
    },
  });

  const rerunMutation = useMutation({
    mutationFn: () => validationService.rerunValidation(Number(runId)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['validationRun', runId] });
    },
  });

  if (isLoading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message="Failed to load validation run" />;
  if (!validationRun) return <ErrorMessage message="Validation run not found" />;

  const formatDuration = (ms: number | null) => {
    if (!ms) return 'N/A';
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  return (
    <Box>
      <Box mb={4}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)} variant="text" sx={{ mb: 2 }}>
          Back
        </Button>
        <Typography variant="h4" component="h1" gutterBottom>
          Validation Run #{validationRun.id}
        </Typography>
        <Typography variant="body1" color="textSecondary">
          {validationRun.pageUrl}
        </Typography>
      </Box>

      <Box mb={3} display="flex" justifyContent="flex-end">
        <Stack direction="row" spacing={1}>
          <Button
            startIcon={<Refresh />}
            onClick={() => rerunMutation.mutate()}
            disabled={rerunMutation.isPending}
            variant="outlined"
          >
            Rerun
          </Button>
        </Stack>
      </Box>

      {/* Status */}
      <Box sx={{ mb: 3 }}>
        <ValidationStatusBadge status={validationRun.status} size="medium" />
      </Box>

      {/* Summary Statistics */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: validationRun.status === 'COMPLETED' ? '#e8f5e9' : '#fff3e0' }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                <ValidationStatusBadge status={validationRun.status} size="small" />
                <Typography variant="caption" color="textSecondary">Status</Typography>
              </Stack>
              <Typography variant="h5" fontWeight="bold">{validationRun.status}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                <ScheduleIcon fontSize="small" color="action" />
                <Typography variant="caption" color="textSecondary">Duration</Typography>
              </Stack>
              <Typography variant="h5" fontWeight="bold">
                {formatDuration(validationRun.durationMs)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#e3f2fd' }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                <CodeIcon fontSize="small" color="action" />
                <Typography variant="caption" color="textSecondary">Total Findings</Typography>
              </Stack>
              <Typography variant="h5" fontWeight="bold" color="primary">
                {validationRun.findings.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: validationRun.failCount > 0 ? '#ffebee' : '#f5f5f5' }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                <ErrorIcon fontSize="small" color="error" />
                <Typography variant="caption" color="textSecondary">Failures</Typography>
              </Stack>
              <Typography variant="h5" fontWeight="bold" color="error">
                {validationRun.failCount}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Findings Breakdown */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, bgcolor: '#ffebee', border: '1px solid #ef5350' }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <ErrorIcon color="error" />
              <Box flex={1}>
                <Typography variant="caption" color="text.secondary">Failures</Typography>
                <Typography variant="h6" fontWeight="bold">
                  {validationRun.findings.filter(f => f.severity === 'FAIL').length}
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, bgcolor: '#fff3e0', border: '1px solid #ff9800' }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <WarningIcon color="warning" />
              <Box flex={1}>
                <Typography variant="caption" color="text.secondary">Warnings</Typography>
                <Typography variant="h6" fontWeight="bold">
                  {validationRun.findings.filter(f => f.severity === 'WARN').length}
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, bgcolor: '#e8f5e9', border: '1px solid #4caf50' }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <CheckCircleIcon color="success" />
              <Box flex={1}>
                <Typography variant="caption" color="text.secondary">Passed</Typography>
                <Typography variant="h6" fontWeight="bold">
                  {validationRun.findings.filter(f => f.severity === 'PASS').length}
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      {/* Run Details */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Run Details
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Stack spacing={2}>
                <Box>
                  <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
                    <ScheduleIcon fontSize="small" color="action" />
                    <Typography variant="caption" color="textSecondary">Run At</Typography>
                  </Stack>
                  <Typography variant="body2" fontWeight={500}>
                    {new Date(validationRun.runAt).toLocaleString()}
                  </Typography>
                </Box>
                <Box>
                  <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
                    <PersonIcon fontSize="small" color="action" />
                    <Typography variant="caption" color="textSecondary">Triggered By</Typography>
                  </Stack>
                  <Typography variant="body2" fontWeight={500}>{validationRun.triggeredBy}</Typography>
                </Box>
              </Stack>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="caption" color="textSecondary" display="block" mb={0.5}>Source</Typography>
                  <Chip label={validationRun.source} color="primary" size="small" variant="outlined" />
                </Box>
                <Box>
                  <Typography variant="caption" color="textSecondary" display="block" mb={0.5}>Rule Version</Typography>
                  <Chip label={validationRun.ruleRegistryVersion} size="small" variant="outlined" />
                </Box>
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Findings */}
      {validationRun.status === 'COMPLETED' && (
        <ValidationFindingsList findings={validationRun.findings} showSeverityFilter />
      )}

      {validationRun.status === 'RUNNING' && (
        <Alert severity="info" icon={<CircularProgress size={20} />}>
          Validation in progress... This page will automatically refresh.
        </Alert>
      )}

      {validationRun.status === 'FAILED' && (
        <Alert severity="error">
          Validation failed. Please try running the validation again.
        </Alert>
      )}
    </Box>
  );
};

export default ValidationDetailPage;
